import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Radio } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  useCommunicateStatus,
  useProvisionCommunicate,
  useUpdateCommunicateConfig,
} from '@/hooks/useClinicCommunicateSettings';
import { ApiCallError } from '@/lib/api';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { StatusChip } from '@/components/ui/Chip';
import { IntegrationsBackLink } from '@/pages/settings/ClinicIntegrations';
import {
  SaveBar,
  SectionCard,
  SettingsFormGrid,
  SettingsLoading,
  SettingsSubpageHeader,
} from './settingsChrome';

export function ClinicCommunicate() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const { data: status, isLoading, error, isError, isFetching, refetch } =
    useCommunicateStatus(activeClinicId);
  const provision = useProvisionCommunicate(activeClinicId);
  const updateConfig = useUpdateCommunicateConfig(activeClinicId);

  const canAdmin = Boolean(
    activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId ?? '')),
  );

  const [sorId, setSorId] = useState('');
  const [ean, setEan] = useState('');
  const [specialityCode, setSpecialityCode] = useState('');

  useEffect(() => {
    if (!status?.connected) {
      setSorId('');
      setEan('');
      setSpecialityCode('');
      return;
    }
    setSorId(status.sorId ?? '');
    setEan(status.ean ?? '');
    setSpecialityCode(status.medicalSpecialityCode ?? '');
  }, [status?.connected, status?.sorId, status?.ean, status?.medicalSpecialityCode]);

  async function handleConnect() {
    if (!activeClinicId) return;
    try {
      const result = await provision.mutateAsync();
      toast.success(
        result.alreadyProvisioned
          ? t('settings.communicate.already_connected')
          : t('settings.communicate.connect_success'),
      );
    } catch (err) {
      console.error('communicate provision failed', err);
      const message =
        err instanceof ApiCallError && err.message
          ? err.message
          : t('settings.communicate.connect_error');
      toast.error(message);
    }
  }

  async function handleSaveConfig() {
    if (!activeClinicId) return;
    try {
      await updateConfig.mutateAsync({
        sorId: sorId.trim() || null,
        ean: ean.trim() || null,
        medicalSpecialityCode: specialityCode.trim() || null,
      });
      toast.success(t('settings.communicate.config_saved'));
    } catch (err) {
      console.error('communicate config update failed', err);
      const message =
        err instanceof ApiCallError && err.message
          ? err.message
          : t('settings.communicate.config_error');
      toast.error(message);
    }
  }

  if (isLoading) {
    return <SettingsLoading />;
  }

  if (error && !status) {
    return <ErrorAlert message={t('settings.communicate.status_error')} />;
  }

  const connected = Boolean(status?.connected);
  const connectionStatus = status?.status ?? null;
  const healthOk = !isError;

  return (
    <div className="flex min-w-0 flex-col gap-s4">
      <IntegrationsBackLink />
      <SettingsSubpageHeader
        icon={<Radio className="h-5 w-5" aria-hidden />}
        title={t('settings.integrations.cards.communicate_title')}
        subtitle={t('settings.communicate.subtitle')}
      />

      {!healthOk && (
        <Banner
          tone="warning"
          action={
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void refetch()}
              loading={isFetching}
              loadingText={t('settings.communicate.retry')}
            >
              {t('settings.communicate.retry')}
            </Button>
          }
        >
          {t('settings.communicate.health_unavailable')}
        </Banner>
      )}

      <SectionCard
        title={t('settings.communicate.connection_title')}
        action={
          <StatusChip tone={connectionChipTone(connected ? connectionStatus : null)}>
            {connected
              ? t(`settings.communicate.status_${connectionStatus ?? 'pending'}`)
              : t('settings.communicate.status_not_connected')}
          </StatusChip>
        }
      >
        {!connected && (
          <div className="flex flex-col gap-s3">
            <p className="text-ds-muted">{t('settings.communicate.not_connected_desc')}</p>
            {canAdmin && (
              <Button
                onClick={handleConnect}
                loading={provision.isPending}
                loadingText={t('settings.communicate.connect')}
              >
                <Radio className="h-4 w-4" aria-hidden />
                {t('settings.communicate.connect')}
              </Button>
            )}
          </div>
        )}

        {connected && (
          <div className="flex flex-col gap-s4">
            <div className="flex items-center gap-s2 text-ds-green-ink">
              <CheckCircle className="h-5 w-5" aria-hidden />
              <span className="type-section">{t('settings.communicate.connected_label')}</span>
            </div>
            <dl className="grid grid-cols-1 gap-s4 nav:grid-cols-2">
              <div>
                <dt className="type-small text-ds-muted">{t('settings.communicate.field_status')}</dt>
                <dd className="type-body text-ds-text">
                  {t(`settings.communicate.status_${connectionStatus ?? 'pending'}`)}
                </dd>
              </div>
              <div>
                <dt className="type-small text-ds-muted">{t('settings.communicate.field_webhook')}</dt>
                <dd className="type-body text-ds-text">
                  {status?.hasWebhookSecret ? t('common.yes') : t('common.no')}
                </dd>
              </div>
              <div>
                <dt className="type-small text-ds-muted">{t('settings.communicate.field_sor')}</dt>
                <dd className="type-body font-mono text-ds-text">{status?.sorId ?? '—'}</dd>
              </div>
              <div>
                <dt className="type-small text-ds-muted">{t('settings.communicate.field_ean')}</dt>
                <dd className="type-body font-mono text-ds-text">{status?.ean ?? '—'}</dd>
              </div>
              <div>
                <dt className="type-small text-ds-muted">{t('settings.communicate.field_speciality')}</dt>
                <dd className="type-body font-mono text-ds-text">
                  {status?.medicalSpecialityCode ?? '—'}
                </dd>
              </div>
            </dl>
          </div>
        )}
      </SectionCard>

      {connected && canAdmin && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSaveConfig();
          }}
        >
          <SectionCard
            title={t('settings.communicate.config_title')}
            subtitle={t('settings.communicate.config_desc')}
          >
            <SettingsFormGrid className="mt-0">
              <Input
                name="communicate-sor"
                label={t('settings.communicate.field_sor')}
                value={sorId}
                onChange={(e) => setSorId(e.target.value)}
                placeholder={t('settings.communicate.field_sor_placeholder')}
                autoComplete="off"
              />
              <Input
                name="communicate-ean"
                label={t('settings.communicate.field_ean')}
                value={ean}
                onChange={(e) => setEan(e.target.value)}
                placeholder={t('settings.communicate.field_ean_placeholder')}
                autoComplete="off"
              />
              <Input
                name="communicate-speciality"
                label={t('settings.communicate.field_speciality')}
                value={specialityCode}
                onChange={(e) => setSpecialityCode(e.target.value)}
                placeholder={t('settings.communicate.field_speciality_placeholder')}
                autoComplete="off"
              />
            </SettingsFormGrid>
          </SectionCard>
          <SaveBar canEdit pending={updateConfig.isPending} />
        </form>
      )}
    </div>
  );
}

function connectionChipTone(status: string | null) {
  switch (status) {
    case 'active':
      return 'completed' as const;
    case 'pending':
      return 'draft' as const;
    case 'disabled':
      return 'cancelled' as const;
    case null:
      return 'neutral' as const;
    default:
      return 'neutral' as const;
  }
}
